/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat, Priska.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import SplitDropdown from 'flarum/common/components/SplitDropdown';
import Button from 'flarum/common/components/Button';
import Separator from 'flarum/common/components/Separator';
import extractText from 'flarum/common/utils/extractText';
import ArticlePickerModal, { type ArticlePickerState } from 'flarum/orell-articles/components/ArticlePickerModal';
import type Mithril from 'mithril';
import editBookshelfAction from '../utils/editBookshelfAction';
import addArticles from '../utils/addArticles';
import isOwnBookshelf from '../helpers/isOwnBookshelf';
import type Bookshelf from '../../common/models/Bookshelf';

export interface BookshelfSettingsMenuAttrs extends ComponentAttrs {
	bookshelf: Bookshelf | null;
}

export default class BookshelfSettingsMenu<
	CustomAttrs extends BookshelfSettingsMenuAttrs = BookshelfSettingsMenuAttrs,
> extends Component<CustomAttrs> {
	view(): Mithril.Children {
		const { bookshelf } = this.attrs;

		if (bookshelf && isOwnBookshelf(bookshelf)) {
			return (
				<SplitDropdown className="BookshelfSettingsMenu" icon="far fa-ellipsis-v" buttonClassName="Button Button--action">
					<Button icon="far fa-plus" className="BookshelfSettingsMenu--addButton" onclick={this.addArticles.bind(this)}>
						{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelf.add_articles')}
					</Button>
					<Button icon="far fa-pencil-alt" onclick={this.editBookshelf.bind(this)}>
						{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelves.rename_bookshelf')}
					</Button>
					<Separator />
					<Button icon="far fa-trash" onclick={this.deleteBookshelf.bind(this)}>
						{app.translator.trans('glowingblue-orell-bookshelf.forum.bookshelves.delete_bookshelf')}
					</Button>
				</SplitDropdown>
			);
		} else {
			return <div></div>;
		}
	}

	addArticles(): void {
		const { bookshelf } = this.attrs;

		app.modal.show(ArticlePickerModal, {
			saveState: (state: ArticlePickerState) => {
				addArticles(bookshelf, state.articles).then(() => {
					app.bookListState?.addArticles(state.articles);
				});
				m.redraw();
			},
			multiSelectable: true,
		});
	}

	editBookshelf(): void {
		const { bookshelf } = this.attrs;

		editBookshelfAction(bookshelf, m.redraw);
	}

	deleteBookshelf(): void {
		const { bookshelf } = this.attrs;

		if (confirm(extractText(app.translator.trans('glowingblue-orell-bookshelf.forum.modal.deletion_confirmation_text')))) {
			bookshelf?.delete().then(() => m.route.set(app.route.book_collections()));
		}
	}
}
